import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import React, { Suspense, lazy } from "react";



// ==================== CONSTANTS ====================
const ROLES = {
  ADMIN: 'admin',
  RECEPTION: 'reception', 
  PATIENT: 'patient',
  PROVIDER: 'provider',
  RIDER: 'rider',
  BRANCH_MANAGER: 'branch_manager'
};

const ROUTES = {
  // Public Routes
  LOGIN: '/login',
  UNAUTHORIZED: '/unauthorized',
  
  // Protected Routes
  DASHBOARD: '/dashboard',
  
  // Admin Routes
  USER_MANAGER: '/usermanager',
  CATEGORIES: '/categoriesUI',
  SERVICES: '/servicesManagement',
  SKILLS: '/skills',
  SKILL_NEW: '/skills/new',
  SKILL_DETAILS: '/skills/details/:id',
  SKILLS_LIST: '/skills/list',
  REQUESTS: '/requests',
  VERIFIED_PROVIDERS: '/verified-providers',
  PROVIDER_INVOICES: '/provider-invoices',
  PATIENTINVOICES  : '/patient-invoices',
  REQUESTS_DASHBOARD: '/requests-dashboard',
  REPORT_CENTER: '/report-center',

  
  
  // Patient Management Routes
  PATIENTS: '/patientsManagement',
  PATIENT_ADD: '/patient-registration',
  
  // Provider Application Routes (Admin only)
  PROVIDER_APPS: '/provider-applications',
  PROVIDER_APP_NEW: '/provider-applications/new',
  PROVIDER_APP_DETAILS: '/provider-applications/details/:id',
  PROVIDER_DOC_UPLOAD: '/provider-applications/upload',
  
//  Verified Provider Routes
 PROVIDER_PROFILE: '/provider-profile',
 PROVIDER_JOBS: '/provider-jobs',
 PROVIDER_BILLS: '/provider-bills',

  // Rider Routes
  DELIVERY_ASSIGNMENTS: '/delivery-assignments',
  DELIVERY_TRACKING: '/delivery-tracking',
  
  // Patient User Routes
  MY_APPOINTMENTS: '/my-appointments',
  MY_PRESCRIPTIONS: '/my-prescriptions',
  MY_RECORDS: '/my-medical-records',
 CREATE_SERVICE_REQUEST: '/create-service-request',
 PATIENT_REQUESTS: '/patient-requests',
 INVOICES_PER_PATIENT: '/invoices-per-patient',
 PATIENT_PROFILE: '/patient-profile',
  
  // Branch Manager Routes
  BRANCH_STOCK: '/branchstock',
  SALES_PANEL: '/salespanel',
  BRANCH_REQUEST: '/branchrequest',
  BRANCH_DISPATCHES: '/branchdispatches',
  
  // Inventory Routes
  HQ_STOCK: '/hqstock',
  PENDING_ORDERS: '/pendingpurchaseorders',
  
  // Reports
  REPORT_CENTER: '/ReportCenter',
     INVOICES_REPORT_CENTER: '/invoices-report-center',
};

// ==================== LAZY LOAD COMPONENTS ====================
// Public Components
const Login = lazy(() => import("./UserProfile/Login"));
const Unauthorized = lazy(() => import("./Components/Unauthorized"));

// Layout Components
const DashboardLayout = lazy(() => import("./pages/DashboardLayout"));
const Dashboard = lazy(() => import("./Dashboard/Dashboard"));

// Admin Components
const UserManager = lazy(() => import("./pages/UserManager"));
const CategoriesUI = lazy(() => import("./pages/CategoriesUI"));
const ServicesManagement = lazy(() => import("./pages/ServicesManagement"));
const SkillsManagement = lazy(() => import("./pages/SkillsManagement"));
const SkillForm = lazy(() => import("./pages/subpages/SkillForm"));
const SkillDetails = lazy(() => import("./pages/subpages/SkillDetails")); 
const SkillsList = lazy(() => import("./pages/subpages/SkillsList"));
const Requests = lazy(() => import("./pages/Admin/Requests"));
const ProviderInvoices = lazy(() => import("./pages/Admin/ProviderInvoices"));
const PatientInvoices = lazy(() => import("./pages/Admin/PatientInvoices"));
const RequestsDashboard = lazy(() => import("./pages/Admin/RequestsDashboard"));
const ReportCenter = lazy(() => import("./pages/ReportCenter"));
const InvoicesReportCenter = lazy(() => import("./pages/InvoicesReportCenter"));



// Patient Management Components
const PatientsManagement = lazy(() => import("./pages/Patient/PatientsManagement"));
const AddPatientPage = lazy(() => import("./pages/Patient/AddPatientPage"));

// Provider Application Components
const ProviderApplications = lazy(() => import("./pages/Provider/ProviderApplications"));
const ApplicationForm = lazy(() => import("./pages/Provider/ApplicationForm"));
const ApplicationDetails = lazy(() => import("./pages/Provider/ApplicationDetails"));
const DocumentUpload = lazy(() => import("./pages/Provider/DocumentUpload"));

// Verified Provider Components
const VerifiedProviders = lazy(() => import("./pages/Provider/ProvidersManagement"));
const ProviderProfile = lazy(() => import("./pages/Provider/ProviderProfile"));
const ProviderJobs = lazy(() => import("./pages/Provider/ProviderJobs"));
const ProviderBills = lazy(() => import("./pages/Provider/ProviderBills"));
 

// Rider Components
const RiderDashboard = lazy(() => import("./pages/Rider/RiderDashboard"));
const DeliveryAssignments = lazy(() => import("./pages/Rider/DeliveryAssignments"));
const DeliveryTracking = lazy(() => import("./pages/Rider/DeliveryTracking"));

// Patient User Components
const MyAppointments = lazy(() => import("./pages/PatientUser/MyAppointments"));
const MyPrescriptions = lazy(() => import("./pages/PatientUser/MyPrescriptions"));
const MyRecords = lazy(() => import("./pages/PatientUser/MyRecords"));
const CreateServiceRequest = lazy(() => import("./pages/PatientUser/CreateServiceRequest"));
const PatientRequests = lazy(() => import("./pages/PatientUser/PatientRequests"));
const InvoicesPerPatient = lazy(() => import("./pages/PatientUser/InvoicesPerPatient"));
const PatientProfile = lazy(() => import("./pages/PatientUser/PatientProfile"));

// ==================== HELPER COMPONENTS ====================
const LoadingSpinner = () => (
  <div className="loading-spinner">
    <div className="spinner"></div>
    <p>Loading application...</p>
  </div>
);

const ProtectedRoute = ({ children, allowedRoles = [] }) => {
  const isAuthenticated = sessionStorage.getItem('loggedIn') === 'true';
  const userRole = sessionStorage.getItem('role');
  
  if (!isAuthenticated) {
    return <Navigate to={ROUTES.LOGIN} replace />;
  }
  
  if (allowedRoles.length > 0 && !allowedRoles.includes(userRole)) {
    return <Navigate to={ROUTES.UNAUTHORIZED} replace />;
  }
  
  return children;
};

const PlaceholderPage = ({ title, role }) => (
  <div style={{ 
    padding: '40px', 
    textAlign: 'center',
    backgroundColor: '#f5f5f5',
    borderRadius: '8px',
    margin: '20px'
  }}>
    <h1>{title}</h1>
    <p>This page is under construction.</p>
    <p>User Role: <strong>{role}</strong></p>
    <button 
      onClick={() => window.history.back()}
      style={{
        padding: '10px 20px',
        backgroundColor: '#1976d2',
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer',
        marginTop: '20px'
      }}
    >
      Go Back
    </button>
  </div>
);

// ==================== MAIN APP COMPONENT ====================
function App() {
  return (
    <Router>
      <Suspense fallback={<LoadingSpinner />}>
        <Routes>
          {/* Public Routes */}
          <Route path={ROUTES.LOGIN} element={<Login />} />
          <Route path={ROUTES.UNAUTHORIZED} element={<Unauthorized />} />
          
          {/* Protected Routes */}
          <Route path="/" element={
            <ProtectedRoute allowedRoles={Object.values(ROLES)}>
              <DashboardLayout />
            </ProtectedRoute>
          }>
            {/* Default redirect */}
            <Route index element={<Navigate to={ROUTES.DASHBOARD} replace />} />
            
            {/* ===== COMMON ROUTES ===== */}
            <Route path={ROUTES.DASHBOARD} element={
              <ProtectedRoute allowedRoles={Object.values(ROLES)}>
                <Dashboard />
              </ProtectedRoute>
            } />
            
            {/* ===== ADMIN ROUTES ===== */}
            <Route path={ROUTES.USER_MANAGER} element={
              <ProtectedRoute allowedRoles={[ROLES.ADMIN]}>
                <UserManager />
              </ProtectedRoute>
            } />
            <Route path={ROUTES.CATEGORIES} element={
              <ProtectedRoute allowedRoles={[ROLES.ADMIN]}>
                <CategoriesUI />
              </ProtectedRoute>
            } />
            <Route path={ROUTES.SERVICES} element={
              <ProtectedRoute allowedRoles={[ROLES.ADMIN]}>
                <ServicesManagement />
              </ProtectedRoute>
            } />

              <Route path={ROUTES.SKILLS} element={
              <ProtectedRoute allowedRoles={[ROLES.ADMIN]}>
                <SkillsManagement />
              </ProtectedRoute>
            } />

            <Route path={ROUTES.SKILL_NEW} element={
              <ProtectedRoute allowedRoles={[ROLES.ADMIN]}>
                <SkillForm />
              </ProtectedRoute>
            } />
            <Route path={ROUTES.SKILL_DETAILS} element={
              <ProtectedRoute allowedRoles={[ROLES.ADMIN]}>
                <SkillDetails />
              </ProtectedRoute>
            } />
            <Route path={ROUTES.SKILLS_LIST} element={
              <ProtectedRoute allowedRoles={[ROLES.ADMIN]}>
                <SkillsList />
              </ProtectedRoute>
            } />
           <Route path={ROUTES.REQUESTS} element={
              <ProtectedRoute allowedRoles={[ROLES.ADMIN]}>
                <Requests />
              </ProtectedRoute>
            } />
            <Route path={ROUTES.PROVIDER_INVOICES} element={
              <ProtectedRoute allowedRoles={[ROLES.ADMIN]}>
                <ProviderInvoices />
              </ProtectedRoute>
            } />
            <Route path={ROUTES.PATIENTINVOICES} element={
              <ProtectedRoute allowedRoles={[ROLES.ADMIN]}>
                <PatientInvoices />
              </ProtectedRoute>
            } />
            <Route path={ROUTES.REQUESTS_DASHBOARD} element={
              <ProtectedRoute allowedRoles={[ROLES.ADMIN]}>
                <RequestsDashboard />
              </ProtectedRoute>
            } />
              <Route path={ROUTES.REPORT_CENTER} element={                                            
              <ProtectedRoute allowedRoles={[ROLES.ADMIN]}>
                <ReportCenter />
              </ProtectedRoute>
            } />
            <Route path={ROUTES.INVOICES_REPORT_CENTER} element={
              <ProtectedRoute allowedRoles={[ROLES.ADMIN]}>
                <InvoicesReportCenter />
              </ProtectedRoute>
            } />

            {/* ===== PATIENT MANAGEMENT ROUTES ===== */}
            
            {/* Patient Management */}
            <Route path={ROUTES.PATIENTS} element={
              <ProtectedRoute allowedRoles={[ROLES.ADMIN, ROLES.RECEPTION]}>
                <PatientsManagement />
              </ProtectedRoute>
            } />
            <Route path={ROUTES.PATIENT_ADD} element={
              <ProtectedRoute allowedRoles={[ROLES.ADMIN, ROLES.RECEPTION]}>
                <AddPatientPage />
              </ProtectedRoute>
            } />
            
            {/* ===== PROVIDER APPLICATION ROUTES (ADMIN ONLY) ===== */}
            <Route path={ROUTES.PROVIDER_APPS} element={
              <ProtectedRoute allowedRoles={[ROLES.ADMIN]}>
                <ProviderApplications />
              </ProtectedRoute>
            } />
            <Route path={ROUTES.PROVIDER_APP_NEW} element={
              <ProtectedRoute allowedRoles={[ROLES.ADMIN]}>
                <ApplicationForm />
              </ProtectedRoute>
            } />
            <Route path={ROUTES.PROVIDER_APP_DETAILS} element={
              <ProtectedRoute allowedRoles={[ROLES.ADMIN]}>
                <ApplicationDetails />
              </ProtectedRoute>
            } />
            <Route path={ROUTES.PROVIDER_DOC_UPLOAD} element={
              <ProtectedRoute allowedRoles={[ROLES.ADMIN]}>
                <DocumentUpload />
              </ProtectedRoute>
            } />

            {/* =====VERIFIED PROVIDER ROUTES  ===== */}
            <Route path={ROUTES.VERIFIED_PROVIDERS} element={
              <ProtectedRoute allowedRoles={[ROLES.ADMIN]}>
                <VerifiedProviders />
              </ProtectedRoute>
            } />
            <Route path={ROUTES.PROVIDER_PROFILE} element={
              <ProtectedRoute allowedRoles={[ROLES.PROVIDER]}>
                <ProviderProfile />
              </ProtectedRoute>
            } />
            
            <Route path={ROUTES.PROVIDER_JOBS} element={
              <ProtectedRoute allowedRoles={[ROLES.PROVIDER]}>
                <ProviderJobs />
              </ProtectedRoute>
            } />  
            <Route path={ROUTES.PROVIDER_BILLS} element={
              <ProtectedRoute allowedRoles={[ROLES.PROVIDER]}>
                <ProviderBills />
              </ProtectedRoute>
            } />    
            
            {/* ===== RIDER ROUTES ===== */}
            <Route path={ROUTES.DASHBOARD} element={
              <ProtectedRoute allowedRoles={[ROLES.RIDER]}>
                <RiderDashboard />
              </ProtectedRoute>
            } />
            <Route path={ROUTES.DELIVERY_ASSIGNMENTS} element={
              <ProtectedRoute allowedRoles={[ROLES.RIDER]}>
                <DeliveryAssignments />
              </ProtectedRoute>
            } />
            <Route path={ROUTES.DELIVERY_TRACKING} element={
              <ProtectedRoute allowedRoles={[ROLES.RIDER]}>
                <DeliveryTracking />
              </ProtectedRoute>
            } />
            
            {/* ===== PATIENT USER ROUTES ===== */}
            <Route path={ROUTES.MY_APPOINTMENTS} element={
              <ProtectedRoute allowedRoles={[ROLES.PATIENT]}>
                <MyAppointments />
              </ProtectedRoute>
            } />
            <Route path={ROUTES.MY_PRESCRIPTIONS} element={
              <ProtectedRoute allowedRoles={[ROLES.PATIENT]}>
                <MyPrescriptions />
              </ProtectedRoute>
            } />
            <Route path={ROUTES.MY_RECORDS} element={
              <ProtectedRoute allowedRoles={[ROLES.PATIENT]}>
                <MyRecords />
              </ProtectedRoute>
            } />
            <Route path={ROUTES.CREATE_SERVICE_REQUEST} element={
              <ProtectedRoute allowedRoles={[ROLES.PATIENT]}>
                <CreateServiceRequest />
              </ProtectedRoute>
            } />
            <Route path={ROUTES.PATIENT_REQUESTS} element={
              <ProtectedRoute allowedRoles={[ROLES.PATIENT]}>
                <PatientRequests />
              </ProtectedRoute>
            } />
              <Route path={ROUTES.INVOICES_PER_PATIENT} element={                         
                <ProtectedRoute allowedRoles={[ROLES.PATIENT]}>
                  <InvoicesPerPatient />
                </ProtectedRoute>
              } />
              <Route path={ROUTES.PATIENT_PROFILE} element={                         
                <ProtectedRoute allowedRoles={[ROLES.PATIENT]}>
                  <PatientProfile />
                </ProtectedRoute>
              } />
            
            {/* ===== BRANCH MANAGER ROUTES ===== */}
            <Route path={ROUTES.BRANCH_STOCK} element={
              <ProtectedRoute allowedRoles={[ROLES.BRANCH_MANAGER]}>
                <PlaceholderPage title="Branch Stock Management" role="branch_manager" />
              </ProtectedRoute>
            } />
            <Route path={ROUTES.SALES_PANEL} element={
              <ProtectedRoute allowedRoles={[ROLES.BRANCH_MANAGER]}>
                <PlaceholderPage title="Sales Panel" role="branch_manager" />
              </ProtectedRoute>
            } />
            <Route path={ROUTES.BRANCH_REQUEST} element={
              <ProtectedRoute allowedRoles={[ROLES.BRANCH_MANAGER]}>
                <PlaceholderPage title="Branch Request to HQ" role="branch_manager" />
              </ProtectedRoute>
            } />
            <Route path={ROUTES.BRANCH_DISPATCHES} element={
              <ProtectedRoute allowedRoles={[ROLES.BRANCH_MANAGER]}>
                <PlaceholderPage title="Branch Dispatches" role="branch_manager" />
              </ProtectedRoute>
            } />
            
            {/* ===== INVENTORY ROUTES ===== */}
            <Route path={ROUTES.HQ_STOCK} element={
              <ProtectedRoute allowedRoles={[ROLES.ADMIN]}>
                <PlaceholderPage title="HQ Stock Management" role="admin" />
              </ProtectedRoute>
            } />
            <Route path={ROUTES.PENDING_ORDERS} element={
              <ProtectedRoute allowedRoles={[ROLES.ADMIN]}>
                <PlaceholderPage title="Pending Purchase Orders" role="admin" />
              </ProtectedRoute>
            } />
            
            {/* ===== REPORT ROUTES ===== */}
            <Route path={ROUTES.REPORT_CENTER} element={
              <ProtectedRoute allowedRoles={[ROLES.ADMIN]}>
                <PlaceholderPage title="Patient Report Center" role="admin" />
              </ProtectedRoute>
            } />

              <Route path={ROUTES.INVOICES_REPORT_CENTER} element={      
              <ProtectedRoute allowedRoles={[ROLES.ADMIN]}>                               
                <PlaceholderPage title="Invoices Report Center" role="admin" />
              </ProtectedRoute>
            } />
            
            {/* Catch-all route */}
            <Route path="*" element={
              <div style={{ padding: '20px' }}>
                <h2>Page Not Found</h2>
                <p>The page you're looking for doesn't exist or you don't have permission to access it.</p>
                <button 
                  onClick={() => window.history.back()}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#1976d2',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Go Back
                </button>
              </div>
            } />
          </Route>
        </Routes>
      </Suspense>
    </Router>
  );
}

export { ROLES, ROUTES };
export default App;